import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Check, X as XIcon, Trash2, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { useLang } from "@/context/LanguageContext";
import { api, fmtDate, fmtEuro, formatApiErrorDetail } from "@/lib/api";

const STATUS_STYLES = {
  new: "bg-stone-100 text-stone-700 border-stone-200",
  negotiating: "bg-amber-100 text-amber-800 border-amber-200",
  accepted: "bg-emerald-100 text-emerald-800 border-emerald-200",
  rejected: "bg-stone-100 text-stone-500 border-stone-200 line-through",
  withdrawn: "bg-stone-100 text-stone-500 border-stone-200 line-through",
};

const emptyOffer = {
  buyer_id: "", buyer_name: "", amount: "", date: new Date().toISOString().slice(0, 10),
  financial_position: "unknown", chain_position: "unknown", proof_of_funds: false,
  status: "new", notes: "",
};

export default function PropertyOffers({ propertyId, propertyName, onStageChanged }) {
  const { t } = useLang();
  const [offers, setOffers] = useState([]);
  const [buyers, setBuyers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(emptyOffer);

  const load = useCallback(async () => {
    if (!propertyId) return;
    setLoading(true);
    try {
      const [o, b] = await Promise.all([
        api.get(`/pipeline/${propertyId}/offers`),
        api.get("/buyers"),
      ]);
      setOffers(o.data);
      setBuyers(b.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  }, [propertyId]);

  useEffect(() => { load(); }, [load]);

  const submitNew = async () => {
    if (!form.amount) return toast.error(t("offerAmountRequired"));
    const buyer = buyers.find((b) => b.id === form.buyer_id);
    try {
      await api.post("/offers", {
        ...form,
        amount: parseFloat(form.amount),
        property_id: propertyId,
        property_name: propertyName || "",
        buyer_name: buyer ? buyer.name : form.buyer_name,
      });
      toast.success(t("offerAdded"));
      setForm(emptyOffer);
      setAdding(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const patchOffer = async (id, patch) => {
    try {
      await api.put(`/offers/${id}`, patch);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const acceptOffer = async (offer) => {
    if (!window.confirm(t("acceptOfferConfirm").replace("{amount}", fmtEuro(offer.amount)))) return;
    try {
      await api.post(`/pipeline/offer/${offer.id}/accept`, {});
      toast.success(t("offerAccepted"));
      load();
      if (onStageChanged) onStageChanged("under_offer");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const removeOffer = async (id) => {
    if (!window.confirm(t("confirmDelete"))) return;
    try {
      await api.delete(`/offers/${id}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div className="space-y-4" data-testid="property-offers-panel">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-heading text-lg text-stone-900">{t("offersTitle")}</h3>
          <p className="text-xs text-stone-500 mt-0.5">{t("offersSubtitle")}</p>
        </div>
        {!adding && (
          <Button
            data-testid="add-offer-button"
            onClick={() => setAdding(true)}
            className="bg-stone-900 hover:bg-stone-800 text-white h-8 text-xs"
          >
            <Plus size={14} className="mr-1" /> {t("addOffer")}
          </Button>
        )}
      </div>

      {adding && (
        <div className="bg-stone-50 border border-stone-200 rounded-xl p-4 space-y-3" data-testid="new-offer-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="offer-buyer">{t("buyer")}</Label>
              <Select value={form.buyer_id || ""} onValueChange={(v) => setForm({ ...form, buyer_id: v })}>
                <SelectTrigger id="offer-buyer" data-testid="offer-buyer-select"><SelectValue placeholder={t("selectBuyer")} /></SelectTrigger>
                <SelectContent>
                  {buyers.map((b) => <SelectItem key={b.id} value={b.id}>{b.ref_code ? `${b.ref_code} · ` : ""}{b.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="offer-amount">{t("offerAmount")}</Label>
              <Input id="offer-amount" data-testid="offer-amount-input" type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="285000" />
            </div>
            <div>
              <Label htmlFor="offer-date">{t("offerDate")}</Label>
              <Input id="offer-date" data-testid="offer-date-input" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
            </div>
            <div>
              <Label htmlFor="offer-financial">{t("financialPosition")}</Label>
              <Select value={form.financial_position} onValueChange={(v) => setForm({ ...form, financial_position: v })}>
                <SelectTrigger id="offer-financial" data-testid="offer-financial-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">{t("finCash")}</SelectItem>
                  <SelectItem value="mortgage_agreed">{t("finMortgageAgreed")}</SelectItem>
                  <SelectItem value="mortgage">{t("finMortgage")}</SelectItem>
                  <SelectItem value="unknown">{t("finUnknown")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="offer-chain">{t("chainPosition")}</Label>
              <Select value={form.chain_position} onValueChange={(v) => setForm({ ...form, chain_position: v })}>
                <SelectTrigger id="offer-chain" data-testid="offer-chain-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="no_chain">{t("chainNone")}</SelectItem>
                  <SelectItem value="top_of_chain">{t("chainTop")}</SelectItem>
                  <SelectItem value="in_chain">{t("chainIn")}</SelectItem>
                  <SelectItem value="unknown">{t("finUnknown")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <label className="inline-flex items-center gap-2 text-sm text-stone-700 cursor-pointer">
                <input type="checkbox" data-testid="offer-proof-checkbox" checked={form.proof_of_funds} onChange={(e) => setForm({ ...form, proof_of_funds: e.target.checked })} />
                {t("proofOfFunds")}
              </label>
            </div>
          </div>
          <div>
            <Label htmlFor="offer-notes">{t("notes")}</Label>
            <textarea id="offer-notes" data-testid="offer-notes-input" rows={2} className="w-full text-sm border border-stone-200 rounded-lg p-2" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" data-testid="cancel-offer-button" onClick={() => { setAdding(false); setForm(emptyOffer); }}>{t("cancel")}</Button>
            <Button data-testid="save-offer-button" onClick={submitNew} className="bg-stone-900 hover:bg-stone-800 text-white">{t("save")}</Button>
          </div>
        </div>
      )}

      {loading && <div className="text-sm text-stone-400 text-center py-4">{t("loading")}...</div>}

      {!loading && offers.length === 0 && !adding && (
        <div className="text-center py-8 text-stone-400 text-sm italic border border-dashed border-stone-200 rounded-xl">
          {t("noOffersYet")}
        </div>
      )}

      <div className="space-y-2">
        {offers.map((offer) => (
          <div key={offer.id} data-testid={`offer-row-${offer.id}`} className={`border rounded-xl p-3 ${offer.status === "accepted" ? "bg-emerald-50/40 border-emerald-200" : "bg-white border-stone-200"}`}>
            <div className="flex flex-wrap items-start gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-base font-semibold text-stone-900">{fmtEuro(offer.amount)}</span>
                  <Badge className={`text-[10px] uppercase ${STATUS_STYLES[offer.status] || STATUS_STYLES.new}`} data-testid={`offer-status-${offer.id}`}>
                    {t(`offerStatus_${offer.status}`)}
                  </Badge>
                  {offer.proof_of_funds && (
                    <Badge className="text-[10px] uppercase bg-emerald-50 border-emerald-200 text-emerald-800">POF ✓</Badge>
                  )}
                </div>
                <div className="text-xs text-stone-500 mt-1 flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center gap-1"><User size={11} />{offer.buyer_name || "—"}</span>
                  <span>·</span>
                  <span>{fmtDate(offer.date)}</span>
                  <span>·</span>
                  <span>{t(`fin_${offer.financial_position}`)}</span>
                  <span>·</span>
                  <span>{t(`chain_${offer.chain_position}`)}</span>
                </div>
                {offer.notes && <p className="text-xs text-stone-600 mt-1.5 whitespace-pre-wrap">{offer.notes}</p>}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {offer.status !== "accepted" && offer.status !== "rejected" && offer.status !== "withdrawn" && (
                  <>
                    <button
                      data-testid={`accept-offer-${offer.id}`}
                      title={t("acceptOffer")}
                      onClick={() => acceptOffer(offer)}
                      className="p-1.5 rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                    >
                      <Check size={14} />
                    </button>
                    <Select value={offer.status} onValueChange={(v) => patchOffer(offer.id, { status: v })}>
                      <SelectTrigger className="h-7 w-32 text-xs" data-testid={`offer-status-select-${offer.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="new">{t("offerStatus_new")}</SelectItem>
                        <SelectItem value="negotiating">{t("offerStatus_negotiating")}</SelectItem>
                        <SelectItem value="rejected">{t("offerStatus_rejected")}</SelectItem>
                        <SelectItem value="withdrawn">{t("offerStatus_withdrawn")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </>
                )}
                <button
                  data-testid={`delete-offer-${offer.id}`}
                  title={t("delete")}
                  onClick={() => removeOffer(offer.id)}
                  className="p-1.5 rounded-lg text-stone-400 hover:text-red-600 hover:bg-red-50"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
